import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Zap, Euro, TrendingUp } from 'lucide-react';
import { useConfigurator } from '../ConfiguratorProvider';
import { panelOptions } from '../productData';

export const PanelSelection = () => {
  const { configuration, setConfiguration } = useConfigurator();

  const handlePanelSelect = (panel: typeof panelOptions[0]) => {
    setConfiguration({ panel });
  };

  const handlePanelCountChange = (value: number[]) => {
    setConfiguration({ panelCount: value[0] });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold mb-2">Kies je zonnepanelen</h3>
        <p className="text-muted-foreground">
          Selecteer het type zonnepaneel dat het beste bij jouw situatie past
        </p>
      </div>

      {/* Panel Count Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Aantal panelen</CardTitle>
          <CardDescription>
            Hoeveel zonnepanelen wil je plaatsen?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Slider
              value={[configuration.panelCount]}
              onValueChange={handlePanelCountChange}
              min={6}
              max={24}
              step={2}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>6 panelen</span>
              <span className="font-medium text-primary text-lg">
                {configuration.panelCount} panelen
              </span>
              <span>24 panelen</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Panel Options */}
      <div className="grid md:grid-cols-2 gap-4">
        {panelOptions.map((panel) => (
          <Card 
            key={panel.id}
            className={`cursor-pointer transition-all hover:shadow-md ${
              configuration.panel?.id === panel.id 
                ? 'ring-2 ring-primary bg-primary/5' 
                : ''
            }`}
            onClick={() => handlePanelSelect(panel)}
          >
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{panel.brand}</CardTitle>
                  <CardDescription>{panel.model}</CardDescription>
                </div>
                <Badge variant="secondary">
                  {panel.efficiency}% efficiënt
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-yellow-500" />
                    <span className="font-medium">{panel.wattage}W</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Euro className="h-4 w-4 text-green-500" />
                    <span className="font-medium">€{panel.price}</span>
                  </div>
                </div>
                
                <div className="bg-muted/50 p-3 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">
                    Totaalprijs ({configuration.panelCount} stuks):
                  </div>
                  <div className="font-bold text-lg">
                    €{(panel.price * configuration.panelCount).toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    0% BTW | {(panel.wattage * configuration.panelCount / 1000).toFixed(1)} kWp totaal
                  </div>
                </div>

                {configuration.panel?.id === panel.id && (
                  <Button className="w-full" variant="secondary">
                    Geselecteerd ✓
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {configuration.panel && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="font-medium">Jouw selectie:</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {configuration.panelCount}x {configuration.panel.brand} {configuration.panel.model} - 
              Totaal {(configuration.panel.wattage * configuration.panelCount / 1000).toFixed(1)} kWp voor 
              €{(configuration.panel.price * configuration.panelCount).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};